import { useState, useMemo, useEffect, useCallback } from 'react'
import { Link, router, useForm, usePage } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import GuardrailAlert from '@/components/GuardrailAlert'
import RecordPaymentModal from '@/components/RecordPaymentModal'
import { LoanBadge } from '@/lib/loanIdentifiers'
import type { LoanProps, PaymentRecord, BorrowerWithLoans } from '@/types/loan'
import type { LoanParticipationSummary, PendingLoanOffering } from '@/types/investor'
import { calculateAmortization, type LoanType } from '@/lib/calculations'

interface GuardrailAlertData {
  key: string
  severity: string
  message: string
  detail: string | null
}

interface LoanDocumentRecord {
  id: number
  document_type: string
  status: 'on_file' | 'missing' | 'expired'
  notes: string | null
  storage_url: string | null
  storage_location: string | null
}

interface Props {
  loan: LoanProps
  total_capital: number
  can_generate_reports: boolean
  guardrails: GuardrailAlertData[]
  loan_documents: LoanDocumentRecord[]
  borrowers_with_loans: BorrowerWithLoans[]
  borrower_email: string | null
  debtlens_invite_sent: boolean
  is_fund_tier: boolean
  participations: LoanParticipationSummary[]
  ownership_total: number
  available_investors: { id: number; name: string }[]
  pending_offerings: PendingLoanOffering[]
}

const STATUS_BADGES: Record<string, { label: string; bg: string; text: string }> = {
  active: { label: 'Active', bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700' },
  paid_off: { label: 'Paid Off', bg: 'bg-gray-100 border-gray-200', text: 'text-gray-600' },
  defaulted: { label: 'Defaulted', bg: 'bg-red-50 border-red-200', text: 'text-red-700' },
  written_off: { label: 'Written Off', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700' },
}

const LOAN_TYPE_LABELS: Record<string, string> = {
  standard: 'Standard',
  interest_only: 'Interest Only',
  balloon: 'Balloon',
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  promissory_note: 'Promissory Note',
  deed_of_trust: 'Deed of Trust',
  title_insurance: 'Title Insurance',
  hazard_insurance: 'Hazard Insurance',
  personal_guarantee: 'Personal Guarantee',
}

const DOC_STATUS_DISPLAY: Record<string, { icon: string; label: string; color: string }> = {
  on_file: { icon: '\u2705', label: 'On File', color: 'text-emerald-700' },
  missing: { icon: '\u26A0\uFE0F', label: 'Missing', color: 'text-amber-700' },
  expired: { icon: '\uD83D\uDD34', label: 'Expired', color: 'text-red-700' },
}

export default function LoansShow({ loan, total_capital, can_generate_reports, guardrails, loan_documents, borrowers_with_loans, borrower_email, debtlens_invite_sent, is_fund_tier, participations, ownership_total, available_investors, pending_offerings }: Props) {
  const { current_user } = usePage<{ current_user: { effective_plan: string } }>().props
  const isPro = current_user?.effective_plan === 'pro' || current_user?.effective_plan === 'fund'

  const [actionsOpen, setActionsOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [editingPayment, setEditingPayment] = useState<PaymentRecord | null>(null)

  // AI states
  const [memoModalOpen, setMemoModalOpen] = useState(false)
  const [memoText, setMemoText] = useState<string | null>(null)
  const [memoLoading, setMemoLoading] = useState(false)
  const [riskNarrative, setRiskNarrative] = useState<string | null>(null)
  const [riskLoading, setRiskLoading] = useState(false)

  const badge = STATUS_BADGES[loan.status] || STATUS_BADGES.active

  const schedule = useMemo(
    () =>
      calculateAmortization(
        loan.principal,
        loan.annual_rate,
        loan.term_months,
        new Date(loan.start_date),
        loan.loan_type as LoanType,
      ).schedule,
    [loan],
  )

  const [alertsExpanded, setAlertsExpanded] = useState(true)
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set())

  // Map server guardrails to display colors, plus add overdue alert (real-time from loan props)
  const allAlerts = useMemo(() => {
    const mapped: { id: string; color: 'red' | 'amber' | 'blue'; message: string; detail: string | null }[] = []

    // Overdue alert from live loan data (not in GuardrailService since it's payment-timing based)
    if (loan.overdue && loan.days_overdue > 0) {
      const expectedAmt = loan.expected_next_payment?.amount
      mapped.push({
        id: 'overdue',
        color: 'red',
        message: `Payment is ${loan.days_overdue} days overdue${expectedAmt ? ` — $${expectedAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })} expected` : ''}.`,
        detail: null,
      })
    }

    // Server-side guardrails
    const severityToColor = (s: string): 'red' | 'amber' | 'blue' => {
      if (s === 'danger' || s === 'red') return 'red'
      if (s === 'warning' || s === 'amber') return 'amber'
      return 'blue'
    }
    for (const g of guardrails) {
      mapped.push({ id: g.key, color: severityToColor(g.severity), message: g.message, detail: g.detail })
    }

    return mapped
  }, [loan, guardrails])

  const visibleAlerts = useMemo(
    () => allAlerts.filter((a) => !dismissedAlerts.has(a.id)),
    [allAlerts, dismissedAlerts],
  )

  const dismissAlert = useCallback((id: string) => {
    setDismissedAlerts((prev) => new Set(prev).add(id))
  }, [])

  // Fetch risk narrative on load (Pro only, async)
  useEffect(() => {
    if (!isPro) return

    setRiskLoading(true)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

    fetch('/api/ai/risk_narrative', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ loan_id: loan.id }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.narrative) {
          setRiskNarrative(data.narrative)
        }
      })
      .catch(() => { /* fail silently */ })
      .finally(() => setRiskLoading(false))
  }, [loan.id, isPro])

  const handleGenerateMemo = useCallback(() => {
    setMemoModalOpen(true)
    setMemoLoading(true)
    setMemoText(null)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

    fetch('/api/ai/deal_memo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ loan_id: loan.id }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setMemoText(data.memo)
        } else {
          setMemoText(null)
          setMemoModalOpen(false)
        }
      })
      .catch(() => {
        setMemoText(null)
        setMemoModalOpen(false)
      })
      .finally(() => setMemoLoading(false))
  }, [loan.id])

  function handleCopyMemo() {
    if (memoText) {
      navigator.clipboard.writeText(memoText)
    }
  }

  function handleDownloadMemoPdf() {
    if (!memoText) return
    // Create a simple text download (true PDF would require a backend endpoint)
    const blob = new Blob([`DEAL MEMO\n${loan.borrower_name}\n${'='.repeat(40)}\n\n${memoText}`], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `deal_memo_${loan.borrower_name.replace(/\s+/g, '_').toLowerCase()}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleDelete() {
    router.delete(`/loans/${loan.id}`, {
      preserveScroll: true,
      onError: () => alert('Failed to delete loan. Please try again.'),
    })
  }

  function handleMarkPaidOff() {
    router.patch(`/loans/${loan.id}/mark_paid_off`, {}, {
      preserveScroll: true,
      onError: () => alert('Failed to update loan status. Please try again.'),
    })
  }

  function handleMarkDefaulted() {
    router.patch(`/loans/${loan.id}/mark_defaulted`, {}, {
      preserveScroll: true,
      onError: () => alert('Failed to update loan status. Please try again.'),
    })
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto">
        {/* Guardrail Alerts */}
        {visibleAlerts.length > 0 && (
          <div className="mb-4">
            {alertsExpanded ? (
              <>
                {visibleAlerts.map((alert) => (
                  <GuardrailAlert key={alert.id} type={alert.color} message={alert.message} detail={alert.detail} onDismiss={() => dismissAlert(alert.id)} />
                ))}
                {visibleAlerts.length > 1 && (
                  <button
                    onClick={() => setAlertsExpanded(false)}
                    className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                  >
                    Collapse alerts
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={() => setAlertsExpanded(true)}
                className="flex items-center gap-2 px-4 py-2.5 w-full rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <span className="font-medium">{visibleAlerts.length} alert{visibleAlerts.length !== 1 ? 's' : ''}</span>
                <svg className="w-3.5 h-3.5 ml-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Link href="/loans" className="text-sm text-gray-400 hover:text-gray-600">
                Loans
              </Link>
              <span className="text-gray-300">/</span>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {loan.borrower_id ? (
                  <Link href={`/borrowers/${loan.borrower_id}`} className="hover:text-emerald-700 transition-colors">
                    {loan.borrower_name}
                  </Link>
                ) : loan.borrower_name}
              </h1>
              <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${badge.bg} ${badge.text}`}>
                {badge.label}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <LoanBadge loanNumber={loan.loan_number} />
              <p className="text-sm text-gray-500">
                {LOAN_TYPE_LABELS[loan.loan_type]} &middot; Started {loan.start_date}
                {loan.purpose && <> &middot; {loan.purpose}</>}
              </p>
            </div>
            {(loan.created_by_team_member_name || loan.updated_by_team_member_name) && (
              <p className="text-xs text-gray-400 mt-1">
                {loan.created_by_team_member_name && (
                  <>Created by <strong>{loan.created_by_team_member_name}</strong> (team member)</>
                )}
                {loan.updated_by_team_member_name &&
                  loan.updated_by_team_member_name !== loan.created_by_team_member_name && (
                  <>
                    {loan.created_by_team_member_name && <> &middot; </>}
                    Last updated by <strong>{loan.updated_by_team_member_name}</strong>
                  </>
                )}
              </p>
            )}
            {borrower_email && !debtlens_invite_sent && (
              <button
                onClick={() => {
                  if (confirm(`Send a DebtLens invitation to ${borrower_email}?`)) {
                    router.post(`/loans/${loan.id}/invite_borrower`)
                  }
                }}
                className="mt-1.5 text-xs text-teal-600 hover:text-teal-700 font-medium"
              >
                Invite borrower to DebtLens
              </button>
            )}
            {borrower_email && debtlens_invite_sent && (
              <p className="mt-1.5 text-xs text-gray-400">DebtLens invite sent</p>
            )}
          </div>

          <div className="relative flex items-center gap-2 flex-wrap">
            {/* AI Deal Memo Button (Pro only) */}
            {isPro ? (
              <button
                onClick={handleGenerateMemo}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
                </svg>
                Deal Memo
              </button>
            ) : (
              <Link
                href="/billing"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-400 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                title="Upgrade to Pro for AI deal memos"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
                AI Memo (Pro)
              </Link>
            )}

            {/* PDF Downloads */}
            {can_generate_reports ? (
              <>
                <a
                  href={`/loans/${loan.id}/statement.pdf`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Download loan statement PDF"
                >
                  <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                  </svg>
                  Statement
                </a>
                <a
                  href={`/loans/${loan.id}/amortization.pdf`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Download amortization schedule PDF"
                >
                  <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M10.875 12c-.621 0-1.125.504-1.125 1.125M12 12c.621 0 1.125.504 1.125 1.125m0 0v1.5c0 .621-.504 1.125-1.125 1.125M12 15.375c0-.621-.504-1.125-1.125-1.125" />
                  </svg>
                  Amortization
                </a>
              </>
            ) : (
              <Link
                href="/billing"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-400 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                title="Upgrade to Pro to download PDF reports"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
                PDF Reports (Pro)
              </Link>
            )}

            {is_fund_tier && loan.status === 'active' && (
              <Link
                href={`/deal_offerings/new?loan_id=${loan.id}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
                Create Offering
              </Link>
            )}

            <Link
              href={`/loans/${loan.id}/edit`}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Edit Loan
            </Link>

            <div className="relative">
              <button
                onClick={() => setActionsOpen(!actionsOpen)}
                className="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                </svg>
              </button>

              {actionsOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-50">
                  {loan.status === 'active' && (
                    <>
                      <button onClick={handleMarkPaidOff} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        Mark as Paid Off
                      </button>
                      <button onClick={handleMarkDefaulted} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        Mark as Defaulted
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                    </>
                  )}
                  <button
                    onClick={() => { setActionsOpen(false); setDeleteConfirm(true) }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Delete Loan
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* AI Risk Narrative (Pro only) */}
        {isPro && (riskLoading || riskNarrative) && (
          <div className="mb-6">
            {riskLoading ? (
              <div className="bg-indigo-50/50 rounded-xl border border-indigo-100 p-5 animate-pulse">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-4 h-4 bg-indigo-200 rounded" />
                  <div className="h-4 w-32 bg-indigo-200 rounded" />
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-indigo-100 rounded w-full" />
                  <div className="h-3 bg-indigo-100 rounded w-5/6" />
                  <div className="h-3 bg-indigo-100 rounded w-4/6" />
                </div>
              </div>
            ) : riskNarrative ? (
              <div className="bg-indigo-50/50 rounded-xl border border-indigo-100 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
                  </svg>
                  <h3 className="text-sm font-semibold text-indigo-700">AI Risk Assessment</h3>
                </div>
                <p className="text-sm text-indigo-900/80 leading-relaxed">{riskNarrative}</p>
              </div>
            ) : null}
          </div>
        )}

        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard label="Principal" value={`$${loan.principal.toLocaleString()}`} />
          <MetricCard label="Rate" value={`${loan.annual_rate}%`} />
          <MetricCard label="Term" value={`${loan.term_months} months`} />
          <MetricCard label="Monthly Payment" value={`$${loan.monthly_payment.toLocaleString('en-US', { minimumFractionDigits: 2 })}`} accent />
        </div>

        {/* Repayment Progress */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Repayment Progress</h3>
            <span className="text-sm font-bold text-emerald-600">{loan.repayment_percentage}%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-700"
              style={{ width: `${loan.repayment_percentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-2">
            <span>{loan.payments_made_count} of {loan.term_months} payments made</span>
            <span>${loan.remaining_balance.toLocaleString('en-US', { minimumFractionDigits: 2 })} remaining</span>
          </div>
        </div>

        {/* Two-Column Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Interest Earned" value={loan.interest_earned} prefix="$" color="text-indigo-600" />
          <StatCard label="Principal Returned" value={loan.principal_returned} prefix="$" color="text-emerald-600" />
          <StatCard label="Remaining Balance" value={loan.remaining_balance} prefix="$" color="text-gray-900" />
          <StatCard label="Next Payment Due" textValue={loan.next_payment_due || 'N/A'} color={loan.overdue ? 'text-red-600' : 'text-gray-900'} />
        </div>

        {/* Amortization Schedule */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="p-5 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Amortization Schedule</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 font-medium text-gray-500 w-8" />
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Month</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Due Date</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">Payment</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">Principal</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">Interest</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">Balance</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row) => {
                  const isPaid = row.month <= loan.payments_made_count
                  return (
                    <tr
                      key={row.month}
                      className={`border-b border-gray-50 ${isPaid ? 'bg-emerald-50/40' : ''}`}
                    >
                      <td className="py-2.5 px-4">
                        {isPaid && (
                          <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </td>
                      <td className={`py-2.5 px-4 ${isPaid ? 'text-emerald-700' : 'text-gray-900'}`}>{row.month}</td>
                      <td className={`py-2.5 px-4 ${isPaid ? 'text-emerald-600' : 'text-gray-600'}`}>{row.dueDate}</td>
                      <td className={`py-2.5 px-4 text-right font-mono ${isPaid ? 'text-emerald-700' : 'text-gray-900'}`}>
                        ${row.payment.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono text-emerald-600">
                        ${row.principalPortion.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono text-indigo-500">
                        ${row.interestPortion.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className={`py-2.5 px-4 text-right font-mono ${isPaid ? 'text-emerald-700' : 'text-gray-900'}`}>
                        ${row.remainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
            {loan.status === 'active' && (
              <button
                onClick={() => setPaymentModalOpen(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
              >
                Record Payment
              </button>
            )}
          </div>

          {loan.payments.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">
              No payments recorded yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {loan.payments.map((p) => (
                <PaymentRow key={p.id} payment={p} onEdit={() => setEditingPayment(p)} />
              ))}
            </div>
          )}
        </div>

        {/* Investors — Fund tier only */}
        {is_fund_tier ? (
          <InvestorsSection
            loanId={loan.id}
            participations={participations}
            ownershipTotal={ownership_total}
            availableInvestors={available_investors}
            pendingOfferings={pending_offerings}
          />
        ) : (
          <InvestorsUpgradeTeaser />
        )}

        {/* Documents on File */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="p-5 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Documents on File</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {loan_documents.map((doc) => (
              <DocumentRow key={doc.id} doc={doc} loanId={loan.id} />
            ))}
          </div>
        </div>

        {/* Collateral & Notes (collapsible) */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <button
            onClick={() => setDetailsOpen(!detailsOpen)}
            className="w-full p-5 flex items-center justify-between text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900">Collateral & Notes</h3>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {detailsOpen && (
            <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Collateral</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {loan.collateral_description || 'None documented'}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Notes</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {loan.notes || 'No notes'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Delete this loan?</h3>
              <p className="text-sm text-gray-500 mb-6">
                This will permanently delete the loan for <strong>{loan.borrower_name}</strong> and
                all associated payment records. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg"
                >
                  Delete Loan
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deal Memo Modal */}
        {memoModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-900">Deal Memo</h3>
                </div>
                <button
                  onClick={() => setMemoModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-xs text-gray-400 mb-4">
                {loan.borrower_name} &middot; ${loan.principal.toLocaleString()} &middot; {loan.annual_rate}% &middot; {loan.term_months}mo
              </p>

              {memoLoading ? (
                <div className="space-y-3 animate-pulse">
                  <div className="h-4 bg-gray-100 rounded w-full" />
                  <div className="h-4 bg-gray-100 rounded w-11/12" />
                  <div className="h-4 bg-gray-100 rounded w-full" />
                  <div className="h-4 bg-gray-100 rounded w-10/12" />
                  <div className="h-4 bg-gray-50 rounded w-full mt-4" />
                  <div className="h-4 bg-gray-50 rounded w-11/12" />
                  <div className="h-4 bg-gray-50 rounded w-9/12" />
                  <div className="h-4 bg-gray-50 rounded w-full mt-4" />
                  <div className="h-4 bg-gray-50 rounded w-10/12" />
                  <div className="h-4 bg-gray-50 rounded w-7/12" />
                  <p className="text-xs text-gray-400 text-center mt-4">Generating deal memo...</p>
                </div>
              ) : memoText ? (
                <>
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {memoText}
                  </div>
                  <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
                    <button
                      onClick={handleCopyMemo}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
                      </svg>
                      Copy
                    </button>
                    <button
                      onClick={handleDownloadMemoPdf}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                      Download
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}

        <RecordPaymentModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          loan={loan}
          borrowersWithLoans={borrowers_with_loans}
        />

        <RecordPaymentModal
          open={!!editingPayment}
          onClose={() => setEditingPayment(null)}
          loan={loan}
          payment={editingPayment ?? undefined}
          borrowersWithLoans={borrowers_with_loans}
        />
      </div>
    </AppLayout>
  )
}

/* ── Sub-components ── */

function MetricCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-lg font-bold ${accent ? 'text-emerald-600' : 'text-gray-900'}`}>{value}</p>
    </div>
  )
}

function StatCard({ label, value, prefix = '', textValue, color }: { label: string; value?: number; prefix?: string; textValue?: string; color: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-lg font-bold font-mono ${color}`}>
        {textValue ?? `${prefix}${(value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
      </p>
    </div>
  )
}

function DocumentRow({ doc, loanId }: { doc: LoanDocumentRecord; loanId: number }) {
  const [editing, setEditing] = useState(false)
  const display = DOC_STATUS_DISPLAY[doc.status] || DOC_STATUS_DISPLAY.missing
  const form = useForm({
    status: doc.status,
    notes: doc.notes || '',
    storage_url: doc.storage_url || '',
    storage_location: doc.storage_location || '',
  })

  const showStorageFields = form.data.status === 'on_file' || form.data.status === 'expired'

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    form.patch(`/loans/${loanId}/documents/${doc.id}`, {
      preserveScroll: true,
      onSuccess: () => setEditing(false),
    })
  }

  return (
    <div className="px-5 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-base shrink-0">{display.icon}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium text-gray-900">
                {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
              </p>
              {doc.storage_url && !editing && (
                <a
                  href={doc.storage_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-emerald-600 shrink-0"
                  title="Open document link"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m9.86-2.54a4.5 4.5 0 0 0-1.242-7.244l-4.5-4.5a4.5 4.5 0 0 0-6.364 6.364L4.34 8.374" />
                  </svg>
                </a>
              )}
              {!doc.storage_url && doc.storage_location && !editing && (
                <span
                  className="text-gray-400 shrink-0 cursor-default"
                  title={doc.storage_location}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                  </svg>
                </span>
              )}
            </div>
            <p className={`text-xs ${display.color}`}>{display.label}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {doc.notes && !editing && (
            <span className="text-xs text-gray-400 hidden sm:inline truncate max-w-[200px]">{doc.notes}</span>
          )}
          <button
            onClick={() => setEditing(!editing)}
            className="text-xs font-medium text-emerald-600 hover:text-emerald-700"
          >
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>
      </div>

      {editing && (
        <form onSubmit={handleSave} className="mt-3 space-y-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
            <div className="w-full sm:w-40">
              <label className="text-xs text-gray-500 mb-1 block">Status</label>
              <select
                value={form.data.status}
                onChange={(e) => form.setData('status', e.target.value as 'on_file' | 'missing' | 'expired')}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="on_file">On File</option>
                <option value="missing">Missing</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div className="flex-1 w-full">
              <label className="text-xs text-gray-500 mb-1 block">Notes</label>
              <input
                type="text"
                value={form.data.notes}
                onChange={(e) => form.setData('notes', e.target.value)}
                maxLength={500}
                placeholder="Optional note (e.g. Policy #1234)"
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          {showStorageFields && (
            <div className="pl-4 border-l-2 border-gray-100 space-y-2">
              <div>
                <label className="text-[11px] text-gray-400 mb-1 block">Document Link</label>
                <input
                  type="url"
                  value={form.data.storage_url}
                  onChange={(e) => form.setData('storage_url', e.target.value)}
                  placeholder="Paste a link to the document (Google Drive, Dropbox, etc.)"
                  className="w-full text-xs text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-400 mb-1 block">Storage Location</label>
                <input
                  type="text"
                  value={form.data.storage_location}
                  onChange={(e) => form.setData('storage_location', e.target.value)}
                  maxLength={255}
                  placeholder="Or describe where it's stored (e.g. office filing cabinet)"
                  className="w-full text-xs text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={form.processing}
              className="px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 shrink-0"
            >
              Save
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

function PaymentRow({ payment, onEdit }: { payment: PaymentRecord; onEdit?: () => void }) {
  return (
    <div className="flex items-center justify-between px-5 py-3 group">
      <div className="flex items-center gap-2">
        <div>
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-gray-900">{payment.date}</p>
            {payment.edited && (
              <span
                className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full"
                title={payment.updated_at ? `Last edited ${new Date(payment.updated_at).toLocaleString()}` : 'Edited'}
              >
                edited
              </span>
            )}
          </div>
          {payment.note && <p className="text-xs text-gray-400 mt-0.5">{payment.note}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <p className="text-sm font-semibold text-gray-900 font-mono">
            ${payment.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-[10px] text-gray-400">
            ${payment.principal_portion.toLocaleString('en-US', { minimumFractionDigits: 2 })} P &middot;{' '}
            ${payment.interest_portion.toLocaleString('en-US', { minimumFractionDigits: 2 })} I
            {payment.late_fee > 0 && <> &middot; ${payment.late_fee.toFixed(2)} fee</>}
          </p>
        </div>
        {onEdit && (
          <button
            onClick={onEdit}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            title="Edit payment"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

function InvestorsSection({
  loanId,
  participations,
  ownershipTotal,
  availableInvestors,
  pendingOfferings = [],
}: {
  loanId: number
  participations: LoanParticipationSummary[]
  ownershipTotal: number
  availableInvestors: { id: number; name: string }[]
  pendingOfferings?: PendingLoanOffering[]
}) {
  const [adding, setAdding] = useState(false)
  const [offering, setOffering] = useState(false)

  // Investors not yet on this loan
  const selectable = useMemo(
    () => availableInvestors.filter((i) => !participations.some((p) => p.investor_id === i.id)),
    [availableInvestors, participations],
  )

  const form = useForm({
    investor_id: '',
    principal_amount: '',
    ownership_percent: '',
  })

  const offeringForm = useForm({
    investor_id: '',
    proposed_principal_amount: '',
    proposed_ownership_percent: '',
    message: '',
    expires_at: '',
  })

  const pendingOwnershipTotal = pendingOfferings.reduce((sum, o) => sum + o.proposed_ownership_percent, 0)
  const remainingPercent = Math.max(0, 100 - ownershipTotal - pendingOwnershipTotal)

  function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    form.post(`/loans/${loanId}/participations`, {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        setAdding(false)
      },
    })
  }

  function handleRemove(participationId: number) {
    if (!confirm('End this participation as of today? Historical distributions are preserved.')) return
    router.delete(`/loans/${loanId}/participations/${participationId}`, { preserveScroll: true })
  }

  function handleSendOffering(e: React.FormEvent) {
    e.preventDefault()
    offeringForm.post(`/loans/${loanId}/offerings`, {
      preserveScroll: true,
      onSuccess: () => {
        offeringForm.reset()
        setOffering(false)
      },
    })
  }

  function handleWithdrawOffering(offeringId: number) {
    if (!confirm('Withdraw this offering?')) return
    router.post(`/loans/${loanId}/offerings/${offeringId}/withdraw`, {}, { preserveScroll: true })
  }

  // Hide entirely if there's nothing to show and no investors exist
  if (participations.length === 0 && availableInvestors.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="p-5">
          <h3 className="text-lg font-semibold text-gray-900">Investors</h3>
          <p className="text-sm text-gray-500 mt-2">
            Track capital partners for this loan.{' '}
            <Link href="/investors/new" className="text-emerald-600 hover:text-emerald-700 font-medium">
              Add an investor
            </Link>{' '}
            to get started.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Investors</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {ownershipTotal.toFixed(2)}% of loan owned by investors
            {pendingOwnershipTotal > 0 && (
              <span className="text-amber-600"> &middot; {pendingOwnershipTotal.toFixed(2)}% pending</span>
            )}
            {' '}&middot; {remainingPercent.toFixed(2)}% unallocated
          </p>
        </div>
        {!adding && !offering && selectable.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAdding(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
            >
              Add Investor
            </button>
            <button
              onClick={() => setOffering(true)}
              className="px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
            >
              Send Offering
            </button>
          </div>
        )}
      </div>

      {participations.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-400">
          No investors on this loan yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 font-medium text-gray-500">Investor</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">Principal Share</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">Ownership %</th>
                <th className="w-16" />
              </tr>
            </thead>
            <tbody>
              {participations.map((p) => (
                <tr key={p.id} className="border-b border-gray-50">
                  <td className="py-2.5 px-4">
                    <Link
                      href={`/investors/${p.investor_id}`}
                      className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
                    >
                      {p.investor_name}
                    </Link>
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                    ${p.principal_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                    {p.ownership_percent.toFixed(2)}%
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    <button
                      onClick={() => handleRemove(p.id)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      End
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <form onSubmit={handleAdd} className="p-5 border-t border-gray-100 bg-gray-50/50 space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Investor</label>
              <select
                value={form.data.investor_id}
                onChange={(e) => form.setData('investor_id', e.target.value)}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
                required
              >
                <option value="">— Select —</option>
                {selectable.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
              {form.errors.investor_id && (
                <p className="mt-1 text-xs text-red-500">{form.errors.investor_id}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Principal Share ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.data.principal_amount}
                onChange={(e) => form.setData('principal_amount', e.target.value)}
                placeholder="0.00"
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
              {form.errors.principal_amount && (
                <p className="mt-1 text-xs text-red-500">{form.errors.principal_amount}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Ownership % (max {remainingPercent.toFixed(2)}%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={remainingPercent}
                value={form.data.ownership_percent}
                onChange={(e) => form.setData('ownership_percent', e.target.value)}
                placeholder="0.00"
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
              {form.errors.ownership_percent && (
                <p className="mt-1 text-xs text-red-500">{form.errors.ownership_percent}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={form.processing}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {form.processing ? 'Adding...' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => {
                form.reset()
                setAdding(false)
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {offering && (
        <form onSubmit={handleSendOffering} className="p-5 border-t border-gray-100 bg-amber-50/30 space-y-4">
          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Send Offering</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Investor</label>
              <select
                value={offeringForm.data.investor_id}
                onChange={(e) => offeringForm.setData('investor_id', e.target.value)}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
                required
              >
                <option value="">— Select —</option>
                {selectable.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Principal Share ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={offeringForm.data.proposed_principal_amount}
                onChange={(e) => offeringForm.setData('proposed_principal_amount', e.target.value)}
                placeholder="0.00"
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Ownership % (max {remainingPercent.toFixed(2)}%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={remainingPercent}
                value={offeringForm.data.proposed_ownership_percent}
                onChange={(e) => offeringForm.setData('proposed_ownership_percent', e.target.value)}
                placeholder="0.00"
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Message (optional)</label>
              <textarea
                value={offeringForm.data.message}
                onChange={(e) => offeringForm.setData('message', e.target.value)}
                rows={2}
                placeholder="Note to investor..."
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Expires (optional)</label>
              <input
                type="datetime-local"
                value={offeringForm.data.expires_at}
                onChange={(e) => offeringForm.setData('expires_at', e.target.value)}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={offeringForm.processing}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {offeringForm.processing ? 'Sending...' : 'Send Offering'}
            </button>
            <button
              type="button"
              onClick={() => {
                offeringForm.reset()
                setOffering(false)
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {pendingOfferings.length > 0 && (
        <div className="border-t border-gray-100">
          <div className="p-4 pb-2">
            <h4 className="text-sm font-semibold text-amber-700">Pending Offerings</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-4 font-medium text-gray-500">Investor</th>
                  <th className="text-right py-2 px-4 font-medium text-gray-500">Principal</th>
                  <th className="text-right py-2 px-4 font-medium text-gray-500">Ownership %</th>
                  <th className="text-left py-2 px-4 font-medium text-gray-500">Sent</th>
                  <th className="w-20" />
                </tr>
              </thead>
              <tbody>
                {pendingOfferings.map((o) => (
                  <tr key={o.id} className="border-b border-gray-50">
                    <td className="py-2 px-4 text-gray-900">{o.investor_name}</td>
                    <td className="py-2 px-4 text-right font-mono text-gray-900">
                      ${o.proposed_principal_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-2 px-4 text-right font-mono text-gray-900">
                      {o.proposed_ownership_percent.toFixed(2)}%
                    </td>
                    <td className="py-2 px-4 text-gray-500">
                      {new Date(o.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-2 px-4 text-right">
                      <button
                        onClick={() => handleWithdrawOffering(o.id)}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Withdraw
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function InvestorsUpgradeTeaser() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
      <div className="p-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-gray-900">Investors</h3>
            <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full">
              Fund Plan
            </span>
          </div>
          <p className="text-sm text-gray-500">
            Track capital partners on this loan, split payments pro-rata, and send year-end statements.
          </p>
        </div>
        <Link
          href="/billing"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors"
        >
          Upgrade to Fund
        </Link>
      </div>
    </div>
  )
}
